import { isDefined, useDictionary } from '@eih-netbroker/common';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocale } from '../../../../context/NbLocalizationContext';
import { CodeInputModal, CodeInputModalLabels } from '../../../../shared/components/code-input-modal/CodeInputModal';
import { ModalHeader } from '../../../../shared/components/modal/ModalHeader';
import { useTransfer } from '../../services/useTransfer';
import { CashActionStep } from '../../states/cashActionAtoms';
import { CashActionReceivedMessages } from '../common/CashActionReceivedMessages';
import { CashActionSummary } from '../common/CashActionSummary';
import classes from './CashTransferModal.module.scss';
import { CashTransferParamEditorWrapper } from './CashTransferParamEditorWrapper';

type CashTransferModalProps = {
  onClose: () => void;
};

export const CashTransferModal = ({ onClose }: CashTransferModalProps) => {
  const { translation } = useLocale();
  const { formatDictionaryType } = useDictionary();
  const {
    currencyOptions,
    handleModalClose,
    handleTargetAccountChange,
    handleTransferConfirm,
    handleTransferSubmit,
    refreshTransferDetails,
    setCashActionTemp,
    selectedTargetAccount,
    selectedSourceAccount,
    handleSourceAccountChange,
    sourceAccountOptions,
    targetAccountOptions,
    cashActionStep,
    cashActionTemp,
    setCashActionStep,
    selectedTransferCurrency,
    smsCodeError,
    canRestartTwoFactor,
    handleTwoFactorRestart,
    setSelectedTransferCurrency,
    transferDetails,
  } = useTransfer();
  const [amount, setAmount] = useState<string>(cashActionTemp?.amount ?? '1');

  const handleCurrencyChange = useCallback(
    (currency: string) => {
      if (!transferDetails.isLoaded) {
        return;
      }

      setSelectedTransferCurrency(currency);
      setAmount('1');
      const firstAvailableSourceAccount = transferDetails.transferPaymentItems.find(
        acc => acc.currencyCode === currency,
      );
      handleSourceAccountChange(firstAvailableSourceAccount ?? '');
    },
    [setSelectedTransferCurrency, transferDetails, handleSourceAccountChange],
  );

  useEffect(() => {
    refreshTransferDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- init effect
  }, []);

  useEffect(() => {
    if (cashActionStep === CashActionStep.Summary && !cashActionTemp) {
      setCashActionStep(CashActionStep.Params);
    }
  }, [cashActionStep, cashActionTemp, setCashActionStep]);

  const handleSubmit = useCallback(() => {
    if (
      !isDefined(selectedSourceAccount) ||
      !isDefined(selectedTargetAccount) ||
      !isDefined(selectedTransferCurrency)
    ) {
      return;
    }

    setCashActionTemp({
      amount,
      targetClientId: selectedTargetAccount.clientId,
      sourceAccountCategory: selectedSourceAccount.accountCategory,
      targetAccountCategory: selectedTargetAccount.accountCategory,
      currencyCode: selectedTransferCurrency,
      sourceClientId: selectedSourceAccount.clientId,
    });
    setCashActionStep(CashActionStep.Summary);
  }, [
    amount,
    selectedTransferCurrency,
    selectedSourceAccount,
    selectedTargetAccount,
    setCashActionStep,
    setCashActionTemp,
  ]);

  const numberInputError = useMemo(() => {
    if (isDefined(selectedSourceAccount)) {
      const parsedAmount = parseFloat(amount);

      if (parsedAmount > selectedSourceAccount.amount) {
        return translation.cashAction.paramEditor.error.transfer.cannotExceedSourceAmount;
      }

      if (parsedAmount === 0) {
        return `${translation.cashAction.paramEditor.error.transfer.cannotBeZero} ${selectedTransferCurrency}!`;
      }
    }
  }, [amount, selectedTransferCurrency, selectedSourceAccount, translation]);

  const codeInputModalLabels = useMemo<CodeInputModalLabels>(
    () => ({
      title: translation.cashAction.confirm.transfer.title,
      description: translation.cashAction.confirm.description,
      inputLabel: translation.cashAction.confirm.smsCodeLabel,
      cancelLabel: translation.general.cancel,
      submitLabel: translation.cashAction.confirm.transfer.submit,
    }),
    [translation],
  );

  if (cashActionStep === CashActionStep.ReceivedMessages) {
    return (
      <CashActionReceivedMessages
        title={translation.cashAction.transfer.title}
        onClose={() => handleModalClose(onClose)}
      />
    );
  }

  if (cashActionStep === CashActionStep.Confirm) {
    return (
      <CodeInputModal
        error={smsCodeError}
        isInitializing={false}
        onSubmit={async (code: string) => handleTransferConfirm(code, () => handleModalClose(onClose))}
        onCancel={() => handleModalClose(onClose)}
        onClose={() => handleModalClose(onClose)}
        onRestart={handleTwoFactorRestart}
        canRestart={canRestartTwoFactor}
        labels={codeInputModalLabels}
      />
    );
  }

  if (cashActionStep === CashActionStep.Summary && isDefined(cashActionTemp)) {
    return (
      <CashActionSummary
        title={translation.cashAction.summary.transfer.title}
        submitButtonText={translation.cashAction.summary.transfer.submit}
        amountLabel={translation.cashAction.summary.transfer.amountLabel}
        onClose={() => handleModalClose(onClose)}
        onBack={() => setCashActionStep(CashActionStep.Params)}
        onSubmit={() => handleTransferSubmit(() => handleModalClose(onClose))}
        sourceAccountText={`${formatDictionaryType(
          cashActionTemp.sourceAccountCategory ?? '',
          'accountCategoryTypes',
        )} - ${cashActionTemp.sourceClientId}`}
        targetAccountText={`${formatDictionaryType(
          cashActionTemp.targetAccountCategory ?? '',
          'accountCategoryTypes',
        )} - ${cashActionTemp.targetClientId}`}
      />
    );
  }

  return (
    <div className={classes.container}>
      <ModalHeader
        title={translation.cashAction.transfer.title}
        onClose={() => handleModalClose(onClose)}
        isSmall
        isWithDivider
        className={classes.header}
      />
      <CashTransferParamEditorWrapper
        amount={amount}
        refreshTransferDetails={refreshTransferDetails}
        transferDetails={transferDetails}
        handleAmountChange={setAmount}
        currencyOptions={currencyOptions}
        handleSubmit={handleSubmit}
        handleTargetAccountChange={handleTargetAccountChange}
        selectedCurrency={selectedTransferCurrency}
        handleCurrencyChange={handleCurrencyChange}
        handleSourceAccountChange={handleSourceAccountChange}
        numberInputError={numberInputError}
        selectedSourceAccount={selectedSourceAccount}
        selectedTargetAccount={selectedTargetAccount}
        sourceAccountOptions={sourceAccountOptions}
        targetAccountOptions={targetAccountOptions}
      />
    </div>
  );
};
